/**
 * Assorted page furniture: sticky header state, flash-sale countdowns,
 * auto-submitting filter controls, and form-submission feedback.
 *
 * These are each too small to justify their own module but share a lifecycle,
 * so they live together rather than as five near-empty files.
 */

import { $$, delegate, on } from '@/lib/dom'

/** Adds `.is-stuck` once the page scrolls, for the header's border + shadow. */
export function initHeader(): void {
  const header = document.querySelector<HTMLElement>('[data-header]')
  if (!header) return

  let ticking = false

  const update = (): void => {
    header.classList.toggle('is-stuck', window.scrollY > 8)
    ticking = false
  }

  on(
    window,
    'scroll',
    () => {
      // Coalesce scroll events into one write per frame.
      if (!ticking) {
        ticking = true
        requestAnimationFrame(update)
      }
    },
    { passive: true },
  )

  update()
}

/** Live countdown for flash-sale banners. */
export function initCountdowns(): void {
  const nodes = $$('[data-countdown]')
  if (nodes.length === 0) return

  const pad = (n: number): string => String(Math.max(0, n)).padStart(2, '0')

  const tick = (): void => {
    nodes.forEach((node) => {
      const target = Date.parse(node.dataset.countdown ?? '')
      if (Number.isNaN(target)) return

      const remaining = Math.max(0, target - Date.now())
      const totalSeconds = Math.floor(remaining / 1000)

      const parts = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      }

      Object.entries(parts).forEach(([unit, value]) => {
        const slot = node.querySelector(`[data-unit="${unit}"]`)
        if (slot) slot.textContent = pad(value)
      })

      if (remaining === 0) node.setAttribute('data-expired', 'true')
    })
  }

  tick()
  setInterval(tick, 1000)
}

/**
 * Filter and sort controls submit their form on change, so there is no
 * "Apply" button to hunt for. The form is a real GET form, which means the
 * result is a shareable, bookmarkable URL — and it still works with JS off.
 */
export function initAutoSubmit(): void {
  delegate(document, 'change', '[data-auto-submit]', (_event, node) => {
    node.closest('form')?.submit()
  })
}

/**
 * Disable submit buttons while a form is in flight.
 *
 * Prevents the classic double-charge: an impatient second click on "Place
 * order" posting the checkout twice.
 */
export function initFormGuards(): void {
  $$('form[data-guard]').forEach((form) => {
    on(form, 'submit', () => {
      $$('button[type="submit"]', form).forEach((button) => {
        button.classList.add('is-loading')
        button.setAttribute('aria-disabled', 'true')
      })
    })
  })
}

/**
 * Billing address fields collapse when "same as shipping" is ticked.
 * Hidden fields are also disabled so the browser does not submit stale values.
 */
export function initBillingToggle(): void {
  const checkbox = document.querySelector<HTMLInputElement>('[data-billing-same]')
  const panel = document.querySelector<HTMLElement>('[data-billing-fields]')
  if (!checkbox || !panel) return

  const sync = (): void => {
    panel.hidden = checkbox.checked
    $$<HTMLInputElement>('input, select', panel).forEach((field) => {
      field.disabled = checkbox.checked
    })
  }

  on(checkbox, 'change', sync)
  sync()
}
